'use strict';
/* ==========================================================================
   levels/jingle.js — step "jingle" (The Jingle, any): THREE AND A BIT, the 1970s roller-disco jingle as a notebook
   lyric video (DESIGN.md §5.5). Model: lessons/balance/levels/jingle.js (same pack, same structure, same controls).

   ► The song: assets/music/jingle.m4a (the one ACE-Step take) + assets/music/jingle_words.json (tools/song_timing.py):
     the TRUE lyric lines of content/jingle.json with word times, and the beat grid {bpm, offset}. NOTHING here
     hard-codes a time: acts are found by the lines' SECTIONS (Intro, Verse 1, Chorus, Verse 2, Bridge, Chorus, Outro),
     the picture runs on the bar grid (one bar = 4 beats of the take's own bpm) or on sung words (S.W(line, word)).
     The karaoke shows only the words of jingle_words.json.

   The whole frame is a pure function of SONG TIME t (the shared <audio> element's clock while it plays; a wall clock
   while audio is locked or missing, or in the tests at __timeScale > 1), so seeking, pausing and stills are exact.

   The picture (every round thing from core-local/circle.js + circlekit.js; exact geometry):
     intro    the title "Three and a Bit", a bike wheel turning slowly (one turn per two bars)
     verses + bridge   the wheel (diameter d = 1 model unit) ROLLS along the floor strip, ONE FULL TURN PER BAR from the
              first bar line of the act: s = turns × Circle.turn(d), drawn with Circle.mark(d, s); the paint spot
              stamps the strip at every full turn (Circle.stamps); under the turn just finished, three "across" sticks
              (d each, Circle.sticks) and the highlighted bit (π − 3 of a d). Three turns fill the strip, then the
              wheel wraps to the start of a clean page of strip.
     chorus   the hook "Three and a bit" writes itself in red at the top. A bigger wheel makes one last turn (one bar)
              and stays: its three sticks and the bit, "around = π × across" on "Around". On the second "Three and a
              bit" a pizza takes over and slices itself (8, then 16 on the second "three", then 32 on its "bit"); on
              "Inside" it unrolls into the near-rectangle (CK.slices with Circle.layout, u eased 0 → 1 over a bar, or less
              when the take leaves under two bars after "Inside"): r tall, half the crust (πr) long, "πr × r = πr²";
              it rolls back up, finishing just before the chorus's page turn (the same length as the unroll).
     outro    the outro line big in red, the wheel turning, the two shorthands; the FULL CIRCLE stamp.
   Karaoke (the index card at the bottom): the current line written on word by word, the sung word under the
   highlighter, the hook words (content/jingle.json "hook") in red pen, the next line faint below, the section small.
   Controls: click / Space play-pause, ↺ restart (R), ⌂ map, ← → seek 5 s, click or drag the strip at the bottom.
   "Finish" appears once ≥ 90% of the song has been heard; the end of a listen with ≥ 90% heard stamps the step.
   Portrait phone: the picture on the stage, Dock.karaoke + ↺ ▶ ⌂ + the strip below.
   Long music (docs/WEBSITE.md §8.4): lesson.json preload.stream names the .m4a, so it plays through the shared
   <audio> element (Audio.media), never decoded into memory. Missing files: a "the jingle is missing" page (no throw).

   QA hooks: sc.song (S(i), E(i), W(i, word), beats, sec), sc.plan.acts, sc.debugAt(t) (freeze at song time t),
   sc.now(), sc.play(t), sc.pause(), sc.toggle(), sc.seek(t), sc.restart(), sc.done(), sc.finish(), sc.lyricAt(t),
   sc.activeWord(t), sc.actAt(t), sc.pictureAt(t) ({act, mode: 'spin' | 'roll' | 'turn' | 'pizza', angle, s, q, page,
   stamps, sticks, n, u, …}), sc.heard, sc.ended, sc.audioReady, sc.canFinish(); Levels.jingle.pictureAt(song, plan,
   t), Levels.jingle.GEOM.
   ========================================================================== */
(function () {
  const STEP = 'jingle';
  const AUDIO = 'music/jingle.m4a', WORDS = 'music/jingle_words.json';
  const KEY = 'jingle:circles';
  const PN = NB.PAL, FONT = NB.FONT;
  const BAND_Y = 846, CUR_Y = 934, NEXT_Y = 1012, CUR_SIZE = 62, NEXT_SIZE = 38;
  const TURN = 0.45;                                   // the page turn between acts (s)
  const FINISH_AT = 0.9;                               // share of the song heard before "Finish" / the stamp
  const CREDIT = 'AI-generated jingle · the math is real';
  const TITLE = 'Three and a Bit';
  const HOOK = 'Three and a bit';
  const STAMP_DEF = 'FULL CIRCLE';

  /* ---------------- the picture's geometry (1920 × 1080 stage; DY moves it down in the portrait dock) ---------------- */
  const D = 1;                                          // the model wheel's diameter (one "across")
  const PAGE = 3;                                       // full turns per page of strip
  const GEOM = {
    roll: { X0: 220, FLOOR: 676, k: 160 },              // k = px per model unit: R = 80 px, a turn ≈ 503 px
    chorus: { FLOOR: 676, k: 220 },                     // the chorus wheel: R = 110 px, centred
    pizza: { cx: 960, cy: 530, r: 168 },
    spin: { cx: 960, cy: 575, R: 160 },
    DY_DOCK: 110,
  };
  const SLICE_N = [8, 16, 32];
  const HOLD = 0.6;                                     // the unrolled rectangle's minimum hold (s)

  /* ---------------- song timing ---------------- */
  const normW = w => String(w).toLowerCase().replace(/[^a-z0-9]/g, '');
  class Song {
    constructor(d) {
      this.d = d; this.lines = d.lines; this.dur = d.duration || 80;
      const b = d.beats || {};
      this.bpm = b.bpm || 116; this.bt = 60 / this.bpm; this.off = b.offset || 0; this.bar = 4 * this.bt;
      this.items = d.lines.map(l => ({ ...l, kind: 'line' })).concat((d.extras || []).map((x, j) => ({ ...x, i: 100 + j }))).sort((a, c) => a.s - c.s);
      this.sec = [];
      d.lines.forEach((l, i) => { const last = this.sec[this.sec.length - 1]; if (last && last.name === l.section) last.lines.push(i); else this.sec.push({ name: l.section, lines: [i] }); });
    }
    section(name, n = 1) { const s = this.sec.filter(x => x.name === name)[n - 1]; return s ? s.lines : []; }
    S(i) { return this.lines[i] ? this.lines[i].s : 1e6; }
    E(i) { return this.lines[i] ? this.lines[i].e : 1e6; }
    /* start of the n-th word of line i that matches `word` (exact, then prefix); the line start if none */
    W(i, word, n = 1) {
      const l = this.lines[i]; if (!l) { if (window.__songCueMiss) window.__songCueMiss.push('?' + i + ':' + word); return 1e6; }
      const k = normW(word);
      for (const pass of [w => normW(w.w) === k, w => normW(w.w).startsWith(k)]) {
        let c = 0;
        for (const w of l.words) if (pass(w) && ++c === n) return w.s;
      }
      if (window.__songCueMiss) window.__songCueMiss.push(i + ':' + word);
      return l.s;
    }
    WE(i, word, n = 1) {
      const s = this.W(i, word, n), l = this.lines[i];
      const w = l && l.words.find(x => x.s === s);
      return w ? w.e : s + 0.3;
    }
    beatPos(t) { return (t - this.off) / this.bt; }
    sinceBeat(t) { const p = this.beatPos(t); return (p - Math.floor(p)) * this.bt; }
    pulse(t, k = 9) { return t < this.off - 0.01 ? 0 : Math.exp(-this.sinceBeat(t) * k); }
  }

  /* ---------------- small drawing helpers ---------------- */
  const fontOk = () => !document.fonts || document.fonts.status === 'loaded';
  const widths = new Map();
  function tw(str, font) {
    const k = font + '|' + str; let w = widths.get(k);
    if (w === undefined) { g.save(); g.font = font; w = g.measureText(str).width; g.restore(); if (fontOk()) { if (widths.size > 2000) widths.clear(); widths.set(k, w); } }
    return w;
  }
  const hand = (size, w = 700) => `${w} ${size}px ${FONT.hand}`;
  function rrect(x, y, w, h, r) { g.beginPath(); g.moveTo(x + r, y); g.arcTo(x + w, y, x + w, y + h, r); g.arcTo(x + w, y + h, x, y + h, r); g.arcTo(x, y + h, x, y, r); g.arcTo(x, y, x + w, y, r); g.closePath(); }
  function alpha(a, fn) { if (a <= 0.001) return; g.save(); g.globalAlpha *= Math.min(1, a); try { fn(); } finally { g.restore(); } }
  /* a label on a small paper backing (x = centre / left / right, y = middle) so it reads over grid lines */
  function plabel(text, x, y, t0, o = {}) {
    if (T < t0) return;
    const size = o.size || 32, f = hand(size), w = tw(text, f), col = o.color || PN.ink;
    const x0 = o.align === 'right' ? x - w : o.align === 'center' ? x - w / 2 : x;
    alpha(P(T, t0, t0 + 0.2), () => { g.fillStyle = PN.page; rrect(x0 - 10, y - size * 0.62, w + 20, size * 1.24, 8); g.fill(); });
    NB.write(text, x0, y, { size, weight: 700, color: col, t0, dur: o.dur || 0.35 });
  }
  /* an ink eighth note (the karaoke's instrumental gap; drawn, not a font glyph) */
  function inkNote(x, y, s, dbl) {
    g.save(); g.translate(x, y); g.scale(s, s); g.fillStyle = PN.ink; g.strokeStyle = PN.ink; g.lineWidth = 4; g.lineCap = 'round';
    const heads = dbl ? [[-16, 14], [20, 6]] : [[0, 14]];
    heads.forEach(([hx, hy]) => { g.beginPath(); g.ellipse(hx, hy, 11, 8, -0.4, 0, TAU); g.fill(); g.beginPath(); g.moveTo(hx + 9, hy - 2); g.lineTo(hx + 9, hy - 46); g.stroke(); });
    g.lineWidth = 6; g.beginPath();
    if (dbl) { g.moveTo(heads[0][0] + 9, heads[0][1] - 46); g.lineTo(heads[1][0] + 9, heads[1][1] - 46); }
    else { g.moveTo(9, -32); g.quadraticCurveTo(26, -20, 22, -4); }
    g.stroke(); g.restore();
  }
  const stampText = () => ((Game.lesson && Game.lesson.completeCard && Game.lesson.completeCard.title) || STAMP_DEF);

  /* ======================================================================
     the picture's STATE at song time t (pure; QA reads it through sc.pictureAt)
     ====================================================================== */
  /* the first bar line of an act (the wheel waits on the strip until it; within a quarter bar of the act's start) */
  function rollStart(S, A) { return S.off + Math.ceil((A.at - S.off) / S.bar - 0.25) * S.bar; }
  /* verses + bridge: one full turn per bar, three turns a page */
  function rollState(S, A, t) {
    const t0 = rollStart(S, A), q = Math.max(0, (t - t0) / S.bar);
    const page = Math.floor(q / PAGE + 1e-9), qp = q - page * PAGE;
    const s = qp * Circle.turn(D), m = Circle.mark(D, s), st = Circle.stamps(D, s);
    const stampAt = j => t0 + (page * PAGE + j) * S.bar;   // the song time stamp j of this page was made
    return { act: A.id, mode: 'roll', t0, q, page, qp, s, m, angle: m.angle, stamps: st, stampAt, sticks: st.length >= 2 ? st.length - 2 : -1 };
  }
  /* the chorus: one last turn, then the pizza (sliced, unrolled, rolled back) */
  function chorusTimes(S, A) {
    if (A._ct) return A._ct;
    const L = A.lines, b = S.bar;
    const tA = A.at + TURN, tAround = L.length > 1 ? S.W(L[1], 'Around') : tA + b;
    const tPz = L.length > 2 ? S.S(L[2]) : A.end, t16 = L.length > 2 ? S.W(L[2], 'three', 2) : A.end, t32 = L.length > 2 ? S.W(L[2], 'bit', 2) : A.end;
    // unroll on "Inside", hold, roll back, all before the page turns (A.end - 0.05): each move gets a full bar when
    // the take leaves two bars and a hold after "Inside"; otherwise both share what is left equally (never collides)
    const tU0 = L.length > 3 ? S.W(L[3], 'Inside') : A.end, last = A.end - 0.05;
    const avail = Math.max(0.3, last - tU0), hold = Math.min(HOLD, avail * 0.2), mv = Math.min(b, (avail - hold) / 2);
    const tU1 = tU0 + mv, tB1 = last, tB0 = tB1 - mv;
    return (A._ct = { tA, tA1: tA + b, tAround, tPz, t16, t32, tU0, tU1, tB0, tB1, move: mv, hold: tB0 - tU1, fade: 0.3 });
  }
  function chorusState(S, A, t) {
    const c = chorusTimes(S, A), q = clamp((t - c.tA) / S.bar, 0, 1), s = q * Circle.turn(D), m = Circle.mark(D, s);
    const n = t < c.t16 ? SLICE_N[0] : t < c.t32 ? SLICE_N[1] : SLICE_N[2];
    const u = t < c.tB0 ? E.inOutCubic(P(t, c.tU0, c.tU1)) : 1 - E.inOutCubic(P(t, c.tB0, c.tB1));
    const mix = P(t, c.tPz - c.fade, c.tPz);
    return { act: A.id, mode: t < c.tPz - c.fade ? 'turn' : 'pizza', q, s, m, angle: m.angle, stamps: Circle.stamps(D, s), n: t < c.tPz ? 0 : n, u, mix, c };
  }
  /* intro / outro: the wheel turning in place, one turn per two bars */
  function spinState(S, A, t) { return { act: A.id, mode: 'spin', angle: TAU * t / (2 * S.bar) }; }
  const STATE = { intro: spinState, verse1: rollState, verse2: rollState, bridge: rollState, chorus1: chorusState, chorus2: chorusState, outro: spinState };

  /* ======================================================================
     drawing (all pure in T = song time)
     ====================================================================== */
  /* the floor strip, the paint, the sticks of the turn just finished, the wheel */
  function drawRoll(S, t, A) {
    const R = rollState(S, A, t), G = GEOM.roll, k = G.k, X0 = G.X0, F = G.FLOOR;
    CK.strip(X0 - 70, F, PAGE * Circle.turn(D) * k + 70 + 110, { torn: 'right', seed: 3 + (R.page % 3) });
    const st = R.stamps;
    for (let j = 1; j < st.length; j++) CK.track(X0 + st[j - 1] * k, X0 + st[j] * k, F, { t0: R.stampAt(j) - 0.02, dur: 0.35, seed: 9 + j });
    for (let j = 0; j < st.length; j++) CK.stamp(X0 + st[j] * k, F, { t0: R.stampAt(j) });
    if (R.sticks >= 0) {
      const j = R.sticks, xa = X0 + st[j] * k, Sx = Circle.sticks(D), t0 = R.stampAt(j + 1), y = F + 34;
      for (let i = 0; i < Sx.whole; i++) CK.stick(xa + i * D * k, xa + (i + 1) * D * k, y, { label: i === 0 ? 'across' : String(i + 1), size: 28, t0: t0 + 0.08 + i * 0.16, dur: 0.22, seed: 300 + i });
      CK.bit(xa + Sx.whole * D * k, xa + Sx.whole * D * k + Sx.restLength * k, y, { t0: t0 + 0.6, size: 30, h: 26 });
    }
    CK.wheel(X0 + R.m.cx * k, F + R.m.cy * k, D / 2 * k, R.angle);
    lyricNotes(S, t, A);
    return R;
  }
  /* above the strip, a short line of maths per sung line (keyed to its word; every claim from facts-circles.md):
     verse 1: a coin, a pizza, a giant ball pop in on their words, then "the same for every one" (rows 2.1, 5.4) */
  const NOTES = {
    verse2: [[1, 'Half', 'half the crust = πr'], [2, 'radius', 'one radius tall: r'], [3, 'Pi', 'area = πr × r = πr²']],
    bridge: [[0, 'Double', 'd = 2 × r'], [1, 'Squared', 'r² = r × r, not r × 2'], [2, 'four', 'twice as wide = 4 × the area'], [3, 'close', '3.14 is a stand-in for π']],
  };
  const DISCS = [['quarter', 'coin', 'a coin'], ['pizza', 'pizza', 'a pizza'], ['spaceship', 'ball', 'a giant ball']];
  function lyricNotes(S, t, A) {
    const L = A.lines;
    if (A.id === 'verse1') {
      if (L.length < 4) return;
      DISCS.forEach(([id, word, label], j) => {
        const t0 = S.W(L[2], word); if (t < t0) return;
        const x = 700 + 260 * j, a = P(t, t0, t0 + 0.2), sc = 0.6 + 0.4 * E.outBack(P(t, t0, t0 + 0.3), 1.6);
        alpha(a, () => CK.disc(id, x, 330, 62 * sc, 0.4 * j));
        NB.write(label, x, 424, { font: 'ui', size: 28, weight: 700, color: PN.pencil, align: 'center', t0, dur: 0.2 });
      });
      const tSame = S.W(L[3], 'same');
      if (t > tSame) plabel('around ÷ across = 3.14… for every one', 960, 478, tSame, { size: 40, align: 'center' });
      return;
    }
    const N = NOTES[A.id]; if (!N) return;
    N.forEach(([li, word, text], j) => { if (L.length <= li) return; const t0 = S.W(L[li], word); if (t > t0) plabel(text, 960, 250 + 68 * j, t0, { size: 44, align: 'center' }); });
  }
  function drawChorus(S, t, A) {
    const C = chorusState(S, A, t), c = C.c, L = A.lines;
    // the hook, in red, at the top
    if (L.length) {
      const t0 = S.S(L[0]) - 0.05, t1 = S.WE(L[0], 'bit');
      NB.write(HOOK, 960, 262, { size: 100, weight: 700, align: 'center', color: PN.redpen, t0, dur: Math.max(0.35, t1 - t0) });
    }
    // the wheel's last turn (fades out as the pizza comes in)
    if (C.mix < 1) alpha(1 - C.mix, () => {
      const G = GEOM.chorus, k = G.k, F = G.FLOOR, turnPx = Circle.turn(D) * k, X0 = 960 - turnPx / 2;
      CK.strip(X0 - 90, F, turnPx + 200, { torn: 'both', seed: 11 });
      const st = C.stamps;
      for (let j = 1; j < st.length; j++) CK.track(X0 + st[j - 1] * k, X0 + st[j] * k, F, { t0: c.tA1 - 0.02, dur: 0.35, seed: 21 });
      for (let j = 0; j < st.length; j++) CK.stamp(X0 + st[j] * k, F, { t0: j ? c.tA1 : c.tA });
      if (st.length >= 2) {
        const Sx = Circle.sticks(D), y = F + 36;
        for (let i = 0; i < Sx.whole; i++) CK.stick(X0 + i * k, X0 + (i + 1) * k, y, { label: i === 0 ? 'across' : String(i + 1), size: 30, t0: c.tA1 + 0.08 + i * 0.16, dur: 0.22, seed: 310 + i });
        CK.bit(X0 + Sx.whole * k, X0 + Sx.whole * k + Sx.restLength * k, y, { t0: c.tA1 + 0.6, size: 32, h: 28 });
      }
      CK.wheel(X0 + C.m.cx * k, F + C.m.cy * k, D / 2 * k, C.angle);
      if (t > c.tAround) plabel('around = π × across', 960, 380, c.tAround, { size: 52, align: 'center' });
    });
    // the pizza: whole → 8 → 16 → 32 slices → unrolled → rolled back
    if (C.mix > 0) alpha(C.mix, () => {
      const Z = GEOM.pizza, r = Z.r;
      if (!C.n) { CK.pizza(Z.cx, Z.cy, r); return; }
      const res = CK.slices(Z.cx, Z.cy, 1, r, C.n, C.u);
      const lab = P(t, c.tU0 + 0.7 * c.move, c.tU1) * (1 - P(t, c.tB0 + 0.2 * c.move, c.tB0 + 0.4 * c.move));
      if (lab > 0 && res && res.box) alpha(lab, () => {
        const b = res.box, sp = Circle.strip(r, C.n), x0 = Z.cx - (C.n - 1) / 2 * r * Math.sin(Math.PI / C.n), x1 = x0 + sp.length;   // the bottom row's crust (n·r·sin(π/n) ≈ π r)
        CK.stick(x0, x1, b.y + b.h + 26, { label: 'πr', labelSide: 'below', size: 40, seed: 77 });
        NB.pen([[b.x - 26, b.y + b.h], [b.x - 26 + 0.5, b.y + b.h - sp.height]], { w: 6, color: PN.redpen, prog: 1 });
        NB.write('r', b.x - 52, b.y + b.h - sp.height / 2, { size: 44, weight: 700, color: PN.redpen, align: 'center' });
        NB.write('πr × r = πr²', b.x + b.w + 40, b.y + b.h / 2, { size: 54, weight: 700, color: PN.ink, t0: c.tU0 + 0.7 * c.move, dur: 0.35 });
      });
    });
    return C;
  }
  function drawSpin(S, t, A, outro) {
    const Z = GEOM.spin, st = spinState(S, A, t);
    if (!outro) NB.title(TITLE, 960, 258, { size: 120, t0: 0.15, hi: 'a Bit' });
    else {
      const L = A.lines, text = L.length ? S.lines[L[0]].text : TITLE, t0 = L.length ? S.S(L[0]) - 0.05 : A.at, t1 = L.length ? S.E(L[0]) : A.at + 1;   // the outro line's last sung word
      const w = tw(text, hand(88)), sz = w > 1300 ? Math.floor(88 * 1300 / w) : 88;
      NB.write(text, 960, 262, { size: sz, weight: 700, align: 'center', color: PN.redpen, t0, dur: Math.max(0.4, t1 - t0) });
    }
    CK.wheel(Z.cx, Z.cy, Z.R, st.angle);
    if (outro) {
      const t0 = A.at + TURN;
      plabel('around = π × d', 470, 500, t0, { size: 50, align: 'center' });
      plabel('inside = π × r²', 1450, 500, t0 + 0.4, { size: 50, align: 'center' });
      const tSt = Math.min(Math.max(t0 + 1.2, A.lines.length ? S.E(A.lines[A.lines.length - 1]) : t0 + 1.2), S.dur - 0.8);
      if (t > tSt) UI.stamp(stampText(), 1450, 690, { s: E.outBack(P(t, tSt, tSt + 0.3), 1.6), rot: -0.12 });
    }
    return st;
  }

  /* ---------------- the acts (by section, from the take's own line times) ---------------- */
  /* (the page itself, NB.ground, is drawn by drawActs before the act's offset) */
  function actIntro(S, t, A) { drawSpin(S, t, A, false); }
  function actRoll(S, t, A) { drawRoll(S, t, A); }
  function actChorus(S, t, A) { drawChorus(S, t, A); }
  function actOutro(S, t, A) { drawSpin(S, t, A, true); }
  function plan(S) {
    const b = i => (i > 0 ? Math.max(S.E(i - 1) - 0.1, S.S(i) - 0.35) : 0);   // never before the previous line's last word
    const acts = [];
    const add = (id, draw, lines, o = {}) => { if (id !== 'intro' && !lines.length) return; acts.push({ id, draw, lines, at: id === 'intro' ? 0 : b(lines[0]), ...o }); };
    add('intro', actIntro, S.section('Intro'));
    add('verse1', actRoll, S.section('Verse 1'));
    add('chorus1', actChorus, S.section('Chorus', 1), { n: 1 });
    add('verse2', actRoll, S.section('Verse 2'));
    add('bridge', actRoll, S.section('Bridge'));
    add('chorus2', actChorus, S.section('Chorus', 2), { n: 2 });
    add('outro', actOutro, S.section('Outro'));
    acts.sort((a, c) => a.at - c.at);
    acts.forEach((a, i) => { a.end = i + 1 < acts.length ? acts[i + 1].at : S.dur; });
    return { acts, ticks: acts.slice(1).map(a => a.at) };
  }
  function actAt(p, t) { let k = 0; for (let i = 0; i < p.acts.length; i++) if (t >= p.acts[i].at) k = i; return k; }
  /* the picture's state at song time t (QA) */
  function pictureAt(S, p, t) {
    const A = p.acts[actAt(p, t)], st = (STATE[A.id] || spinState)(S, A, t);
    const out = { act: A.id, mode: st.mode, angle: st.angle };
    if (st.mode === 'roll') Object.assign(out, { t0: st.t0, q: st.q, page: st.page, qp: st.qp, s: st.s, stamps: st.stamps.length, sticks: st.sticks, cx: st.m.cx });
    if (A.id === 'chorus1' || A.id === 'chorus2') Object.assign(out, { q: st.q, s: st.s, stamps: st.stamps.length, n: st.n, u: st.u, mix: st.mix, times: st.c });
    return out;
  }
  /* the notebook page turn: the old page slides up off the top (the next page is already underneath) */
  function turn(drawOld, drawNew, t, t0, dur) {
    const p = E.inOutCubic(P(t, t0, t0 + dur));
    drawNew();
    if (p >= 1) return;
    const dy = -1100 * p;
    g.save(); g.beginPath(); g.rect(0, 0, 1920, 1080 + dy); g.clip(); g.translate(0, dy); drawOld(); g.restore();
    g.save(); g.fillStyle = 'rgba(29,43,79,0.12)'; g.fillRect(0, 1080 + dy, 1920, 8); g.restore();
  }
  function drawActs(S, p, t, dy) {
    const k = actAt(p, t), A = p.acts[k];
    const run = i => () => {
      g.save(); NB.ground(); g.translate(0, dy);
      try { p.acts[i].draw(S, t, p.acts[i]); } finally { g.restore(); g.globalAlpha = 1; }
    };
    if (k > 0 && t < A.at + TURN) turn(run(k - 1), run(k), t, A.at, TURN);
    else run(k)();
  }

  /* ---------------- karaoke (only the words of jingle_words.json) ---------------- */
  let HOOKS = null;
  const hooksText = () => (((Game.content || {}).jingle || {}).hook || []);
  const hooks = () => HOOKS || (HOOKS = hooksText().map(l => l.split(/\s+/).map(normW).filter(Boolean)));
  function hookIdx(item) {
    const ws = item.words, out = new Set();
    if (item.section !== 'Chorus' && item.section !== 'Outro') return out;
    for (const h of hooks()) for (let k = 0; k + h.length <= ws.length; k++) if (h.every((w, j) => normW(ws[k + j].w) === w)) h.forEach((_, j) => out.add(k + j));
    return out;
  }
  const layouts = new Map();
  function layout(item, size) {
    const key = item.i + '|' + item.s + '|' + size;
    if (layouts.has(key)) return layouts.get(key);
    const f = hand(size), sp = tw(' ', f), hk = hookIdx(item);
    let x = 0;
    const toks = item.words.map((w, j) => { const ww = tw(w.w, f), tk = { text: w.w, s: w.s, e: w.e, x, w: ww, hook: hk.has(j) }; x += ww + sp; return tk; });
    const out = { toks, width: x - sp, size };
    if (fontOk()) layouts.set(key, out);
    return out;
  }
  const wordEnd = (ws, j) => { const nx = ws[j + 1]; return Math.min(Math.max(ws[j].e, ws[j].s + 0.18) + 0.05, nx ? nx.s - 0.02 : 1e9); };
  function drawCurrent(item, t, y, maxW = 1640) {
    let size = CUR_SIZE, Lo = layout(item, size);
    if (Lo.width > maxW) { size = Math.floor(size * maxW / Lo.width); Lo = layout(item, size); }
    const x0 = 960 - Lo.width / 2, appear = item.appear ?? item.s - 0.5, a = E.outCubic(P(t, appear, appear + 0.25));
    const yy = y + (1 - a) * 30;
    Lo.toks.forEach((tk, j) => {
      const x = x0 + tk.x, sung = t >= tk.s, active = t >= tk.s - 0.02 && t < wordEnd(Lo.toks, j);
      if (active) NB.highlight(x - 8, yy - size * 0.42, tk.w + 16, size * 0.84, { t0: tk.s - 0.02, dur: 0.12 });
      if (!sung || t < tk.s + 0.3) NB.write(tk.text, x, yy, { size, weight: 700, color: PN.pencilLight, alpha: a });
      if (sung) NB.write(tk.text, x, yy, { size, weight: 700, color: tk.hook ? PN.redpen : PN.ink, t0: tk.s, dur: clamp(tk.e - tk.s, 0.08, 0.3) });
    });
  }
  /* a line comes up 0.5 s before its first word, but never before the previous line has been sung */
  const appearAt = (it, k) => (k === 0 ? it[0].s - 0.5 : Math.min(it[k].s, Math.max(it[k].s - 0.5, it[k - 1].e + 0.05)));
  function lyricAt(song, t) {
    const it = song.items;
    let cur = -1;
    for (let k = 0; k < it.length; k++) if (t >= appearAt(it, k)) cur = k;
    let c = cur >= 0 ? it[cur] : null;
    if (c) c.appear = appearAt(it, cur);
    const nx = it[cur + 1] || null;
    if (c && t > c.e + 2.2 && (!nx || nx.s - t > 1.2)) c = null;       // a long instrumental gap: clear the line
    return { cur: c, next: nx && nx.s - t < 8 ? nx : null };
  }
  function activeWord(song, t) {
    const { cur } = lyricAt(song, t); if (!cur) return null;
    const ws = cur.words;
    for (let j = 0; j < ws.length; j++) if (t >= ws[j].s - 0.02 && t < wordEnd(ws, j)) return { line: cur.i, j, w: ws[j].w, s: ws[j].s, e: ws[j].e };
    return null;
  }
  /* after the last line: the chorus's first two hook lines (sung words only) */
  const endNote = () => hooksText().slice(0, 2).join('. ') + '.';
  function band(song, t) {
    NB.indexCard(40, BAND_Y, 1840, 260, { header: 44, pitch: 78 });
    const { cur, next } = lyricAt(song, t);
    const sec = (cur || next || {}).section;
    if (sec) NB.write(sec.toUpperCase(), 70, BAND_Y + 23, { font: 'ui', size: 22, weight: 700, color: PN.pencil });
    if (cur) drawCurrent(cur, t, CUR_Y);
    if (next) {
      let size = NEXT_SIZE; const w = tw(next.text, hand(size, 400));
      if (w > 1640) size = Math.floor(size * 1640 / w);
      NB.write(next.text, 960, NEXT_Y, { size, weight: 400, color: PN.pencil, align: 'center', alpha: 0.8 });
    } else if (song.items.length && t > song.items[song.items.length - 1].e) {
      NB.write(endNote(), 960, NEXT_Y, { size: 40, weight: 700, color: PN.pencil, align: 'center', t0: song.items[song.items.length - 1].e + 0.3 });
    }
    if (!cur && !next && t > 0.6) {
      const b = ((Math.floor(song.beatPos(t)) % 3) + 3) % 3, pu = song.pulse(t, 6);
      for (let i = 0; i < 3; i++) inkNote(860 + i * 100, CUR_Y - 22 * pu * (i === b ? 1 : 0.15), 1, i === 1);
    }
  }

  /* ======================================================================
     the scene
     ====================================================================== */
  class Jingle extends Scene {
    enter() {
      this.song = null; this.plan = null; this.err = null;
      this.playing = false; this.frozen = false; this.ended = false; this.startedOnce = false;
      this.pos = 0; this.t0ctx = null; this.heard = 0;
      this.audioReady = false; this.completed = false; this.gone = false;
      Audio.stopMusic();
      try {                                                   // bake the sprites the song will draw, off the hot path
        CK.warm(() => {
          for (const R of [GEOM.roll.k / 2, GEOM.chorus.k / 2, GEOM.spin.R]) CK.wheel(0, 0, R, 0);
          CK.pizza(0, 0, GEOM.pizza.r);
          for (const [id] of DISCS) CK.disc(id, 0, 0, 62, 0);
          for (const n of SLICE_N) CK.slices(0, 0, 1, GEOM.pizza.r, n, 0);
        });
      } catch (e) { console.warn('[jingle] warm', e && e.message); }
      const wurl = Assets.url(WORDS);
      const words = (window.Loader ? Loader.json(wurl).then(d => d || Promise.reject(new Error('no timings'))) : fetch(wurl).then(r => (r.ok ? r.json() : Promise.reject(new Error('HTTP ' + r.status)))))
        .then(d => { if (!d || !Array.isArray(d.lines) || !d.lines.length) throw new Error('empty timings'); this.song = new Song(d); this.plan = plan(this.song); })
        .catch(e => { this.err = String(e && e.message); console.warn('[jingle] no timings', this.err); });
      const audio = (Audio.has(KEY) ? Promise.resolve() : Audio.load({ [KEY]: Assets.url(AUDIO) })).then(() => { this.audioReady = Audio.has(KEY); }, e => { console.warn('[jingle] no audio', e && e.message); });
      Promise.all([words, audio]).then(() => { if (!this.gone && !this.frozen && !this.startedOnce && this.song) this.play(0); });
      if (window.Device) {
        this.onHidden = Device.on('hidden', () => { if (this.playing && !this.gone) { this.pause(); this.resumeOnReturn = true; } });
        this.onResume = Device.on('resume', () => { if (this.resumeOnReturn && !this.gone && Game.current() === this) { this.resumeOnReturn = false; this.play(this.pos); } });
      }
    }
    exit() {
      this.gone = true; Audio.stopGroup('song'); this.playing = false;
      if (this.media) this.media.pause();
      if (window.Device) { Device.off('hidden', this.onHidden); Device.off('resume', this.onResume); }
    }
    get key() { return KEY; }
    back() { Game.replace(new Levels.map()); }
    /* the song clock: the <audio> element while it plays (t0ctx != null = "on the audio clock"), else pos */
    now() {
      if (this.playing && this.t0ctx != null) {
        const m = this.media;
        if (m) return m.playing() ? m.time() : m.el && m.el.ended ? this.song.dur : this.pos;
        if (Audio.running()) return Audio.now() - this.t0ctx;
      }
      return this.pos;
    }
    play(from = this.now()) {
      if (!this.song) return;
      this.startedOnce = true; this.frozen = false; this.ended = false;
      from = clamp(from, 0, this.song.dur - 0.05);
      Audio.stopGroup('song');
      this.pos = from; this.playing = true; this.t0ctx = null;
      if (!this.audioReady || window.__timeScale > 1) return;
      this.media = this.media || Audio.media(KEY);
      if (this.media) {
        const m = this.media, tok = this.playTok = (this.playTok || 0) + 1;
        this.playAt = Game.time; this.blocked = false;
        m.play(from).then(ok => {
          if (tok !== this.playTok || this.gone) return;
          if (ok && this.playing) { this.pos = m.time(); this.t0ctx = 0; }
          else if (!ok) { this.pause(); this.blocked = true; }
        });
      } else if (Audio.running()) {
        const at = Audio.now() + 0.06;
        Audio.schedule(KEY, { at, offset: from, gain: 1, group: 'song', bus: 'voice', fade: 0.03 });
        this.t0ctx = at - from;
      }
    }
    pause() {
      this.pos = this.now(); this.playing = false; this.t0ctx = null; this.playTok = (this.playTok || 0) + 1;
      Audio.stopGroup('song');
      if (this.media) this.media.pause();
    }
    toggle() { if (this.playing) this.pause(); else this.play(this.ended ? 0 : this.pos); Audio.play('paper', { gain: 0.25 }); }
    seek(t) { if (!this.song) return; const was = this.playing; this.pause(); this.pos = clamp(t, 0, this.song.dur); this.ended = false; if (was) this.play(this.pos); }
    restart() { this.heard = 0; this.play(0); }
    /* QA: freeze on song time t (no pause overlay) */
    debugAt(t) { this.pause(); this.frozen = true; this.startedOnce = true; this.ended = false; this.pos = t; }
    /* stamp the step now (QA / integration; ≥ 90% heard does this at the end, or the Finish button) */
    done() { if (this.completed) return; this.completed = true; this.pause(); if (!Game.progress.stamps[STEP]) Game.complete(STEP); }
    canFinish() { return !!this.song && !this.completed && this.heard >= FINISH_AT * this.song.dur; }
    finish() { if (this.canFinish()) { Audio.play('stamp', { gain: 0.6 }); this.done(); } }
    lyricAt(t) { return this.song ? lyricAt(this.song, t) : null; }
    activeWord(t) { return this.song ? activeWord(this.song, t) : null; }
    actAt(t) { return this.plan ? this.plan.acts[actAt(this.plan, t)].id : null; }
    pictureAt(t) { return this.plan ? pictureAt(this.song, this.plan, t) : null; }

    update(dt) {
      if (!this.song || !this.playing || this.scrub) return;
      if (this.t0ctx == null) {
        this.pos += dt * (window.__timeScale || 1);
        const retry = this.audioReady && !(window.__timeScale > 1) &&
          (this.media ? !this.blocked && Game.time - (this.playAt || 0) > 3 : Audio.running());
        if (retry) this.play(this.pos);
      }
      this.heard += dt * (this.t0ctx == null ? (window.__timeScale || 1) : 1);
      if (this.now() >= this.song.dur - 0.05) {
        this.pause(); this.pos = this.song.dur; this.ended = true;
        if (!this.completed && this.heard >= FINISH_AT * this.song.dur) this.done();
      }
    }
    keyDown(k) {
      if (!this.song) return;
      if (k === ' ') this.toggle();
      else if (k === 'r' || k === 'R') this.restart();
      else if (k === 'ArrowRight') this.seek(this.now() + 5);
      else if (k === 'ArrowLeft') this.seek(this.now() - 5);
      else if (k === 'Enter' && this.ended) this.back();
    }
    stripZone() { return Math.max(32, UI.tap(32)); }
    pointerDown(x, y) {
      if (!this.song || this.ended) return;
      if (UI.docked()) { this.toggle(); return; }
      if (UI.inHud(x, y, 3)) return;
      if (this.canFinish() && this._finBox && Math.abs(x - this._finBox.x) < this._finBox.w / 2 && Math.abs(y - this._finBox.y) < this._finBox.h / 2) return;
      if (y > 1080 - this.stripZone()) {
        this.scrub = { was: this.playing };
        if (this.playing) this.pause();
        this.pos = clamp(x / 1920 * this.song.dur, 0, this.song.dur - 0.05); this.ended = false;
        return;
      }
      this.toggle();
    }
    pointerMove(x) { if (this.scrub && this.song) this.pos = clamp(x / 1920 * this.song.dur, 0, this.song.dur - 0.05); }
    pointerUp() {
      if (!this.scrub) return;
      const was = this.scrub.was; this.scrub = null;
      if (was) this.play(this.pos);
    }

    draw() {
      const docked = UI.docked(), title = (Game.content.jingle && Game.content.jingle.title) || TITLE;
      if (!this.song) {
        NB.ground();
        NB.title(title, 960, 360, { size: 110, t0: this.enteredAt + 0.2, hi: 'a Bit' });
        const msg = this.err ? 'the jingle is missing (' + this.err + ')' : 'warming up the band…';
        NB.write(msg, 960, 520, { font: 'ui', size: 44, color: PN.pencil, align: 'center', t0: this.enteredAt + 0.4 });
        if (docked) { Dock.head(title, ''); Dock.text('jg-wait', [msg], { cls: 'note center' }); this.dockBar(); }
        else this.chrome(null);
        return;
      }
      const t = clamp(this.now(), 0, this.song.dur), gT = T, gBF = BF;
      T = t; BF = Math.floor(t * 8);
      try {
        drawActs(this.song, this.plan, t, docked ? GEOM.DY_DOCK : 0);
        if (!docked) band(this.song, t);
      } finally { T = gT; BF = gBF; }
      if (docked) this.dock(t, title); else this.chrome(t);
    }

    /* ---------- the portrait dock: karaoke, controls and the strip below the stage ---------- */
    dock(t, title) {
      const S = this.song, pl = this.plan;
      const mmss = s => Math.floor(s / 60) + ':' + String(Math.floor(s % 60)).padStart(2, '0');
      Dock.head(title, `${mmss(t)} / ${mmss(S.dur)}`);
      const { cur, next } = lyricAt(S, t);
      const sec = (cur || next || {}).section;
      const lastE = S.items.length ? S.items[S.items.length - 1].e : 0;
      if (cur) {
        const key = cur.i + ':' + cur.s;
        if (!this._dk || this._dk.key !== key) {
          const hk = hookIdx(cur);
          this._dk = { key, words: cur.words.map((w, j) => ({ w: w.w, s: w.s, e: w.e, hook: hk.has(j) })) };
        }
        Dock.karaoke({ key, words: this._dk.words, t, next: next ? next.text : '', section: sec ? sec.toLowerCase() : '' });
      } else Dock.karaoke({ key: 'gap', words: [], t, next: next ? next.text : '', section: sec ? sec.toLowerCase() : '',
        note: t > lastE && S.items.length ? endNote() : this.startedOnce || this.frozen ? '♪ ♫ ♪' : 'loading the jingle…' });
      Dock.text('credit', ['♪ ' + CREDIT], { cls: 'small center' });
      if (this.canFinish() && !this.ended) Dock.buttons('jg-fin', [{ label: 'Finish', color: PN.redpen, onClick: () => this.finish() }]);
      if (this.ended && Game.current() === this) {
        Dock.buttons('jg-end', [{ label: 'Play it again', onClick: () => this.restart() },
          { label: 'Back to the map', color: PN.ink, onClick: () => this.back() }]);
      }
      Dock.strip({ value: t, total: S.dur, ticks: pl.ticks, label: 'jingle position', onSeek: (v, phase) => this.scrubTo(v, phase) });
      this.dockBar();
      if (!this.playing && !this.frozen && this.startedOnce && !this.ended && !this.scrub) this.pausedMark(540, 150);
    }
    dockBar() {
      if (this.song && !this.ended) {
        Dock.button({ id: 'restart', glyph: '↺', label: 'restart', aria: 'play the jingle from the start', onClick: () => this.restart() });
        Dock.button({ id: 'play', glyph: this.playing ? '❚❚' : '▶', label: this.playing ? 'pause' : 'play', aria: this.playing ? 'pause' : 'play', primary: true, onClick: () => this.toggle() });
      }
      Dock.button({ id: 'map', glyph: '⌂', label: 'map', aria: 'back to the map', onClick: () => this.back() });
    }
    scrubTo(v, phase) {
      if (!this.song) return;
      const pos = clamp(v, 0, this.song.dur - 0.05);
      if (phase === 'start') { this.scrub = { was: this.playing || (this.scrub && this.scrub.was) }; if (this.playing) this.pause(); this.ended = false; }
      this.pos = pos;
      if (phase === 'end') { const was = this.scrub ? this.scrub.was : false; this.scrub = null; if (was) this.play(this.pos); }
    }
    /* the paused mark: a white wash and an ink circle with a drawn triangle */
    pausedMark(y, r) {
      g.save(); g.fillStyle = 'rgba(252,252,248,0.55)'; g.fillRect(0, 0, 1920, UI.docked() ? 1080 : BAND_Y); g.restore();
      g.save(); g.fillStyle = PN.ink; g.beginPath(); g.arc(960, y, r, 0, TAU); g.fill();
      g.fillStyle = PN.page; g.beginPath(); g.moveTo(960 - r * 0.3, y - r * 0.52); g.lineTo(960 + r * 0.52, y); g.lineTo(960 - r * 0.3, y + r * 0.52); g.closePath(); g.fill(); g.restore();
    }

    chrome(t) {
      const t0 = this.enteredAt, S = this.song;
      const intro = t == null || (this.plan && this.plan.acts.length > 1 && t < this.plan.acts[1].at);   // the intro page writes the big title itself
      if (!intro) NB.write(TITLE, 190, 124, { size: 46, weight: 700, t0: -1e9 });
      NB.write(CREDIT, 190, intro ? 112 : 176, { font: 'ui', size: 26, color: PN.pencil, t0: t0 + 0.4, dur: 0.3 });
      const m = UI.hudSlot(0), r = UI.hudSlot(1), pl = UI.hudSlot(2), fx = { r: m.r, fixed: true };
      if (UI.iconButton('jg-map', '⌂', m.x, m.y, fx)) { this.back(); return; }
      if (S) {
        if (UI.iconButton('jg-restart', '↺', r.x, r.y, fx)) this.restart();
        if (UI.iconButton('jg-play', this.playing ? '❚❚' : '▶', pl.x, pl.y, { ...fx, primary: true })) this.toggle();
      }
      if (t == null) return;
      // Finish: once ≥ 90% of the song has been heard (below the HUD, top right)
      this._finBox = null;
      if (this.canFinish() && !this.ended) {
        const w = UI.tap(250), h = UI.tap(84), fy = m.y + m.r + 30 + h / 2, fxp = m.x + m.r - w / 2;
        this._finBox = { x: fxp, y: fy, w, h };
        if (UI.button('jg-finish', 'Finish', fxp, fy, { w, h, size: UI.text(40), primary: true })) { this.finish(); return; }
      }
      const zone = this.stripZone(), touch = zone > 32;
      if (!this.ended) UI.target('jg-strip', 960, 1080 - zone / 2, 1920, zone);
      const sh = touch ? 18 : 10, sy = 1080 - sh;
      g.save();
      g.fillStyle = PN.pencilLight; g.fillRect(0, sy, 1920, sh);
      g.fillStyle = PN.ink; g.fillRect(0, sy, 1920 * t / S.dur, sh);
      g.fillStyle = PN.redpen; this.plan.ticks.forEach(b => g.fillRect(1920 * b / S.dur - 2, sy - 6, 4, sh + 6));
      g.restore();
      if (touch || this.scrub) NB.pencil(clamp(1920 * t / S.dur, 26, 1894), sy, touch ? 0.9 : 0.6);
      if (!this.playing && !this.frozen && this.startedOnce && !this.ended) {
        this.pausedMark(430, 100);
        NB.write(UI.hint('paused · click or Space to play', 'paused · tap to play'), 960, 590, { font: 'ui', size: 44, weight: 700, align: 'center' });
      }
      if (!this.startedOnce && !this.frozen) NB.write('loading the jingle…', 960, 590, { font: 'ui', size: 44, color: PN.pencil, align: 'center', t0: this.enteredAt + 0.3 });
      if (this.ended && Game.current() === this) {
        g.save(); g.fillStyle = 'rgba(252,252,248,0.6)'; g.fillRect(0, 380, 1920, 360); g.restore();
        if (UI.button('jg-again', 'Play it again', 700, 560, { w: 440, h: 110, size: 50, secondary: true })) this.restart();
        if (UI.button('jg-back', 'Back to the map', 1220, 560, { w: 440, h: 110, size: 50, primary: true })) this.back();
      }
    }
  }
  Jingle.STEP = STEP;
  Jingle.Song = Song;
  Jingle.plan = plan;
  Jingle.pictureAt = pictureAt;
  Jingle.rollStart = rollStart;
  Jingle.GEOM = GEOM;
  Jingle.PAGE = PAGE;
  Jingle.SLICE_N = SLICE_N;
  Jingle.KEY = KEY;
  Levels.jingle = Jingle;
})();
